import { useEffect, useState } from 'react';
import { Briefcase, ChevronDown, ChevronRight, Hash, Loader2 } from '../../icons';
import {
  listDivisions, listTeams, listAllTeams, getTeam,
  type DivisionDto, type TeamDto, type TeamMemberDto,
} from '../../api/organization';
import { useDeterministicColor } from '../../hooks/useDeterministicColor';
import { getSharedLang, type Lang } from '../../lib/base-i18n';
// 손짠 머리 → 정본 PageHeader(2026-10-17 · web 레인 W7c — 쪽 머리 한 꼴 F4)
import { PageHeader } from '../../components/shared';
import './OrganizationPage.css';

// Cycle 53 — local i18n dict (cycle 49 MfaVerifyPage / cycle 52 TaskPage·LoginPage 패턴).
// fallback chain: lang → en → ko (cycle 46 학습 phase 5).
const ORG_MESSAGES: Record<Lang, {
  err_fetch: string;
  page_title: string;
  page_subtitle: string;
  aria_tree: string;
  root_name: string;
  divisions_count_suffix: string;
  teams_count_suffix: string;
  members_count_suffix: string;
  loading: string;
  empty_divisions: string;
  empty_teams: string;
  empty_members: string;
  leader_badge: string;
}> = {
  ko: {
    err_fetch: '조직 조회 실패',
    page_title: '조직도',
    page_subtitle: '회사 구성원과 부서를 확인하세요',
    aria_tree: '조직 트리',
    root_name: '우리 기관',
    divisions_count_suffix: '부서',
    teams_count_suffix: '팀',
    members_count_suffix: '명',
    loading: '로딩…',
    empty_divisions: '등록된 부서가 없습니다',
    empty_teams: '팀이 없습니다',
    empty_members: '멤버가 없습니다',
    leader_badge: '팀장',
  },
  en: {
    err_fetch: 'Failed to load organization',
    page_title: 'Organization',
    page_subtitle: 'View company members and divisions',
    aria_tree: 'Organization tree',
    root_name: 'Our institution',
    divisions_count_suffix: 'divisions',
    teams_count_suffix: 'teams',
    members_count_suffix: 'members',
    loading: 'Loading…',
    empty_divisions: 'No divisions registered',
    empty_teams: 'No teams',
    empty_members: 'No members',
    leader_badge: 'Lead',
  },
  ja: {
    err_fetch: '組織取得失敗',
    page_title: '組織図',
    page_subtitle: '会社のメンバーと部署を確認してください',
    aria_tree: '組織ツリー',
    root_name: '当機関',
    divisions_count_suffix: '部署',
    teams_count_suffix: 'チーム',
    members_count_suffix: '名',
    loading: '読み込み中...',
    empty_divisions: '登録された部署がありません',
    empty_teams: 'チームがありません',
    empty_members: 'メンバーがいません',
    leader_badge: 'リーダー',
  },
};

function tOrg(): typeof ORG_MESSAGES['ko'] {
  return ORG_MESSAGES[getSharedLang()] ?? ORG_MESSAGES.en;
}

/**
 * OrganizationPage (P3-ORG-01) — 시안 line 2532~2575 매핑.
 *
 * 트리 구조: Institution → Divisions → Teams → Members.
 * lazy load — division expand 시 listTeams, team expand 시 getTeam (members 포함).
 *
 * 후속:
 *   - 검색 (사용자 이름 / 부서 / 팀 — Phase 1 P1-11 의 GET /users?search 연결).
 *   - 사용자 클릭 → 프로필 modal.
 *   - 인원 수 집계 — 본 사이클은 멤버 expand 후 길이 표시 (전체 사전 집계는 후속 backend endpoint).
 */
export function OrganizationPage() {
  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDiv, setExpandedDiv] = useState<Set<string>>(new Set());
  const [teamsByDiv, setTeamsByDiv] = useState<Record<string, TeamDto[]>>({});
  const [expandedTeam, setExpandedTeam] = useState<Set<string>>(new Set());
  const [membersByTeam, setMembersByTeam] = useState<Record<string, TeamMemberDto[]>>({});
  const [busyTeam, setBusyTeam] = useState<string | null>(null);
  // 부서에 속하지 않는 팀 — 부서를 거치는 조회로는 볼 수 없어 따로 읽는다(migration 100).
  const [looseTeams, setLooseTeams] = useState<TeamDto[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const [ds, all] = await Promise.all([listDivisions(), listAllTeams()]);
        setDivisions(ds);
        setLooseTeams(all.filter((t) => t.divisionId === null));
      } catch (err) {
        setError((err as Error)?.message ?? tOrg().err_fetch);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function toggleDivision(div: DivisionDto) {
    const isOpen = expandedDiv.has(div.id);
    if (isOpen) {
      setExpandedDiv((s) => { const n = new Set(s); n.delete(div.id); return n; });
      return;
    }
    setExpandedDiv((s) => new Set(s).add(div.id));
    if (!teamsByDiv[div.id]) {
      try {
        const ts = await listTeams(div.id);
        setTeamsByDiv((c) => ({ ...c, [div.id]: ts }));
      } catch {
        setTeamsByDiv((c) => ({ ...c, [div.id]: [] }));
      }
    }
  }

  async function toggleTeam(team: TeamDto) {
    const isOpen = expandedTeam.has(team.id);
    if (isOpen) {
      setExpandedTeam((s) => { const n = new Set(s); n.delete(team.id); return n; });
      return;
    }
    setExpandedTeam((s) => new Set(s).add(team.id));
    if (!membersByTeam[team.id]) {
      setBusyTeam(team.id);
      try {
        const detail = await getTeam(team.id);
        setMembersByTeam((c) => ({ ...c, [team.id]: detail.members }));
      } catch {
        setMembersByTeam((c) => ({ ...c, [team.id]: [] }));
      } finally {
        setBusyTeam((cur) => (cur === team.id ? null : cur));
      }
    }
  }

  const t = tOrg();
  return (
    <div className="org-page">
      <div className="org-container">
        <PageHeader
          breadcrumb={[t.page_title]}
          title={t.page_title}
          description={t.page_subtitle}
        />

        {error && (
          <div className="org-error" role="alert">{error}</div>
        )}

        <section className="org-tree" aria-label={t.aria_tree}>
          <div className="org-tree-root">
            <Briefcase size={18} aria-hidden />
            <span className="org-tree-root-name">{t.root_name}</span>
            <span className="org-tree-root-count">{t.divisions_count_suffix} {divisions.length}개</span>
          </div>

          {loading && (
            <div className="org-loading">
              <Loader2 size={14} className="org-spin" /> {t.loading}
            </div>
          )}

          {!loading && divisions.length === 0 && looseTeams.length === 0 && !error && (
            <div className="org-empty">{t.empty_divisions}</div>
          )}

          <ul className="org-list">
            {/* 부서 없는 팀은 부서와 같은 깊이(루트 바로 아래)에 놓는다 — 그 조직에서는
                이것이 곧 최상위 단위다. 가짜 부서로 감싸면 없는 계층을 지어내게 된다. */}
            {looseTeams.map((team) => (
              <li className="org-division" key={`loose-${team.id}`}>
                <ul className="org-team-list">
                  <TeamRow
                    team={team}
                    isOpen={expandedTeam.has(team.id)}
                    members={membersByTeam[team.id]}
                    busy={busyTeam === team.id}
                    onToggle={() => toggleTeam(team)}
                  />
                </ul>
              </li>
            ))}
            {divisions.map((div) => (
              <DivisionRow
                key={div.id}
                division={div}
                isOpen={expandedDiv.has(div.id)}
                teams={teamsByDiv[div.id]}
                membersByTeam={membersByTeam}
                expandedTeam={expandedTeam}
                busyTeam={busyTeam}
                onToggle={() => toggleDivision(div)}
                onToggleTeam={toggleTeam}
              />
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

interface DivisionRowProps {
  division: DivisionDto;
  isOpen: boolean;
  teams: TeamDto[] | undefined;
  membersByTeam: Record<string, TeamMemberDto[]>;
  expandedTeam: Set<string>;
  busyTeam: string | null;
  onToggle: () => void | Promise<void>;
  onToggleTeam: (team: TeamDto) => void | Promise<void>;
}

function DivisionRow({ division, isOpen, teams, membersByTeam, expandedTeam, busyTeam, onToggle, onToggleTeam }: DivisionRowProps) {
  const t = tOrg();
  return (
    <li className="org-division">
      <button
        type="button"
        className="org-division-row"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="org-division-name">{division.name}</span>
        {teams && (
          <span className="org-division-count">{t.teams_count_suffix} {teams.length}개</span>
        )}
      </button>
      {isOpen && (
        <ul className="org-team-list">
          {!teams && (
            <li className="org-team-loading"><Loader2 size={12} className="org-spin" /> {t.loading}</li>
          )}
          {teams && teams.length === 0 && (
            <li className="org-team-empty">{t.empty_teams}</li>
          )}
          {teams && teams.map((team) => (
            <TeamRow
              key={team.id}
              team={team}
              isOpen={expandedTeam.has(team.id)}
              members={membersByTeam[team.id]}
              busy={busyTeam === team.id}
              onToggle={() => onToggleTeam(team)}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

interface TeamRowProps {
  team: TeamDto;
  isOpen: boolean;
  members: TeamMemberDto[] | undefined;
  busy: boolean;
  onToggle: () => void | Promise<void>;
}

function TeamRow({ team, isOpen, members, busy, onToggle }: TeamRowProps) {
  const t = tOrg();
  return (
    <li className="org-team">
      <button
        type="button"
        className="org-team-row"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Hash size={12} className="org-team-hash" />
        <span className="org-team-name">{team.name}</span>
        {members && (
          <span className="org-team-count">{members.length}{t.members_count_suffix}</span>
        )}
      </button>
      {isOpen && (
        <div className="org-member-block">
          {busy && (
            <div className="org-member-loading"><Loader2 size={12} className="org-spin" /> {t.loading}</div>
          )}
          {members && members.length === 0 && (
            <div className="org-member-empty">{t.empty_members}</div>
          )}
          {members && members.length > 0 && (
            <div className="org-member-chips">
              {members.map((m) => (
                <MemberChip key={m.id} member={m} />
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function MemberChip({ member }: { member: TeamMemberDto }) {
  // 결정적 색상 — string id → number.
  const userIdNum = Number.parseInt(member.user.id, 10) || 0;
  const color = useDeterministicColor(userIdNum);
  const initial = (member.user.name?.charAt(0) ?? '?').toUpperCase();
  return (
    <div
      className={`org-member-chip ${member.isLeader ? 'org-member-chip--leader' : ''}`}
      title={member.user.position ? `${member.user.name} · ${member.user.position}` : member.user.name}
    >
      <span className="org-member-chip-avatar" style={{ background: color }}>{initial}</span>
      <span className="org-member-chip-name">{member.user.name}</span>
      {member.isLeader && <span className="org-member-chip-leader">{tOrg().leader_badge}</span>}
    </div>
  );
}
